import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { ReactNode, useState, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  optionalBooleanSchema,
  optionalEmailSchema,
  optionalFirstNameSchema,
  optionalIdSchema,
  optionalLastNameSchema,
  optionalMiddleNameSchema,
  optionalPhoneSchema,
  optionalStringSchema,
  optionalZipSchema,
} from '~/utils/zodSchemas.ts';
import { CustomerFormDeal } from './utils/formHelper.ts';
import { customerFormEventsName } from './constant.ts';
import { FormMode } from '~/components/molecules/index.ts';

const contactSchema = z.object({
  firstName: optionalFirstNameSchema,
  lastName: optionalLastNameSchema,
  middleName: optionalMiddleNameSchema,
  email: optionalEmailSchema,
  homePhone: optionalPhoneSchema,
  mobilePhone: optionalPhoneSchema,
  hasCoapplicant: optionalBooleanSchema,
});

export type ContactSchema = z.infer<typeof contactSchema>;

const leadSchema = z.object({
  type: optionalStringSchema,
  sourceId: optionalIdSchema,
  sourceName: optionalStringSchema,
  secondaryId: optionalIdSchema,
  secondarySourceName: optionalStringSchema,
  trackingCode: optionalIdSchema,
  trackingCodeName: optionalStringSchema,
});

export type LeadSchema = z.infer<typeof leadSchema>;

const addressSchema = z.object({
  streetAddress: optionalStringSchema,
  apt: optionalStringSchema,
  city: optionalStringSchema,
  state: optionalStringSchema,
  zip: optionalZipSchema,
  county: optionalStringSchema,
});

export type AddressSchema = z.infer<typeof addressSchema>;

const requiredFieldConfig = z
  .object({
    firstName: optionalBooleanSchema,
    middleName: optionalBooleanSchema,
    lastName: optionalBooleanSchema,
    email: optionalBooleanSchema,
    phone: optionalBooleanSchema,
    zip: optionalBooleanSchema,
    source: optionalBooleanSchema,
    trackingCode: optionalBooleanSchema,
  })
  .optional();

export type RequiredFieldConfig = z.infer<typeof requiredFieldConfig>;

const externalValidations = z
  .object({
    zipDoesNotExist: optionalBooleanSchema,
    coapplicantZipDoesNotExist: optionalBooleanSchema,
  })
  .optional();

/*
  Configurable Required Fields
  -Email
  -Phone
  -ZipCode
  -Tracking Code

  Default Required Fields
  -First Name
  -Last Name
  -Lead Type - if Crm Type not Stand Alone
*/

export const customerSchema = z
  .object({
    isInShowroom: z.boolean(),
    addCoapplicantToggle: z.coerce.boolean(),
    contact: contactSchema,
    lead: leadSchema,
    address: addressSchema,
    coapplicant: contactSchema,
    coapplicantAddress: addressSchema,
    requiredFieldConfig: requiredFieldConfig.optional(),
    externalValidations: externalValidations,
  })
  .superRefine((schema, ctx) => {
    const {
      contact,
      lead,
      address,
      coapplicant,
      coapplicantAddress,
      requiredFieldConfig,
      addCoapplicantToggle,
      externalValidations,
    } = schema;
    const { firstName, lastName, middleName, email, mobilePhone } = contact;
    const {
      firstName: coapplicantFirstName,
      middleName: coapplicantMiddleName,
      lastName: coapplicantLastName,
      email: coapplicantEmail,
      mobilePhone: coapplicantMobilePhone,
    } = coapplicant;
    const isAddingCoapplicant = addCoapplicantToggle;
    const { type, sourceId, trackingCode } = lead;
    const { zip } = address;
    const { zip: coapplicantZip } = coapplicantAddress;
    if (requiredFieldConfig?.firstName && !firstName?.trim()) {
      ctx.addIssue({
        path: ['contact.firstName'],
        code: 'custom',
        message: 'Enter a valid first name',
      });
    }

    if (requiredFieldConfig?.middleName && !middleName?.trim()) {
      ctx.addIssue({
        path: ['contact.middleName'],
        code: 'custom',
        message: 'Enter a valid middle name',
      });
    }

    if (requiredFieldConfig?.lastName && !lastName?.trim()) {
      ctx.addIssue({
        path: ['contact.lastName'],
        code: 'custom',
        message: 'Enter a valid last name',
      });
    }

    if (requiredFieldConfig?.email && !email?.trim()) {
      ctx.addIssue({
        path: ['contact.email'],
        code: 'custom',
        message: 'Enter a valid email',
      });
    }

    if (requiredFieldConfig?.phone && !mobilePhone?.trim()) {
      ctx.addIssue({
        path: ['contact.mobilePhone'],
        code: 'custom',
        message: 'Must be 10 digits',
      });
    }

    if (requiredFieldConfig?.source && !type) {
      ctx.addIssue({
        path: ['lead.type'],
        code: 'custom',
        message: 'Select lead type',
      });
    }
    if (requiredFieldConfig?.source && !!type !== !!sourceId) {
      ctx.addIssue({
        path: ['lead.sourceId'],
        code: 'custom',
        message: 'Select a lead source',
      });
    }
    if (requiredFieldConfig?.trackingCode && !trackingCode) {
      ctx.addIssue({
        path: ['lead.trackingCode'],
        code: 'custom',
        message: 'Select a tracking code',
      });
    }
    if (
      (requiredFieldConfig?.zip && !zip?.trim()) ||
      externalValidations?.zipDoesNotExist
    ) {
      ctx.addIssue({
        path: ['address.zip'],
        code: 'custom',
        message: 'Enter a valid ZIP',
      });
    }

    if (isAddingCoapplicant) {
      if (requiredFieldConfig?.firstName && !coapplicantFirstName?.trim()) {
        ctx.addIssue({
          path: ['coapplicant.firstName'],
          code: 'custom',
          message: 'Enter a valid first name',
        });
      }
      if (requiredFieldConfig?.middleName && !coapplicantMiddleName?.trim()) {
        ctx.addIssue({
          path: ['coapplicant.middleName'],
          code: 'custom',
          message: 'Enter a valid middle name',
        });
      }
      if (requiredFieldConfig?.lastName && !coapplicantLastName?.trim()) {
        ctx.addIssue({
          path: ['coapplicant.lastName'],
          code: 'custom',
          message: 'Enter a valid last name',
        });
      }
      if (requiredFieldConfig?.email && !coapplicantEmail?.trim()) {
        ctx.addIssue({
          path: ['coapplicant.email'],
          code: 'custom',
          message: 'Enter a valid email',
        });
      }
      if (requiredFieldConfig?.phone && !coapplicantMobilePhone?.trim()) {
        ctx.addIssue({
          path: ['coapplicant.mobilePhone'],
          code: 'custom',
          message: 'Must be 10 digits',
        });
      }
      if (
        (requiredFieldConfig?.zip && !coapplicantZip?.trim()) ||
        externalValidations?.coapplicantZipDoesNotExist
      ) {
        ctx.addIssue({
          path: ['coapplicantAddress.zip'],
          code: 'custom',
          message: 'Enter a valid ZIP',
        });
      }
    }
  });
export type CustomerSchema = z.infer<typeof customerSchema>;

export const defaultRequiredFieldsConfig = {
  firstName: true,
  lastName: true,
};
export const defaultCustomerValues: CustomerSchema = {
  isInShowroom: false,
  addCoapplicantToggle: false,
  contact: {
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    homePhone: '',
    mobilePhone: '',
  },
  lead: {
    type: '',
    sourceId: '',
    sourceName: '',
    secondaryId: '',
    secondarySourceName: '',
    trackingCode: '',
    trackingCodeName: '',
  },
  address: {
    streetAddress: '',
    apt: '',
    city: '',
    state: '',
    zip: '',
    county: '',
  },
  coapplicant: {
    firstName: '',
    lastName: '',
    middleName: '',
    email: '',
    homePhone: '',
    mobilePhone: '',
  },
  coapplicantAddress: {
    streetAddress: '',
    apt: '',
    city: '',
    state: '',
    zip: '',
    county: '',
  },
  requiredFieldConfig: defaultRequiredFieldsConfig,
};

export const CustomerSaveContext = React.createContext<{
  onSubmit?: (updatedDeal: CustomerFormDeal) => void;
  onTrack?: (event: CustomerFormEventType) => void;
  onClose: () => void;
  showUnsavedDialog: boolean;
  setShowUnsavedDialog: React.Dispatch<React.SetStateAction<boolean>>;
  formMode?: FormMode;
  setFormMode: (mode: FormMode) => void;
}>({
  onSubmit: (): void => {},
  onTrack: (): void => {},
  onClose: (): void => {},
  showUnsavedDialog: false,
  setShowUnsavedDialog: (): void => {},
  formMode: undefined,
  setFormMode: (): void => {},
});

export type CustomerFormEventType =
  (typeof customerFormEventsName)[keyof typeof customerFormEventsName];

interface CustomerFormProviderProps {
  children: ReactNode;
  onSubmit?: (updatedDeal: CustomerFormDeal) => void;
  onTrack?: (event: CustomerFormEventType) => void;
  onClose: () => void;
}

const CustomerFormProvider: React.FC<CustomerFormProviderProps> = ({
  children,
  onSubmit,
  onTrack,
  onClose,
}) => {
  const form = useForm<CustomerSchema>({
    mode: 'onTouched',
    defaultValues: defaultCustomerValues,
    resolver: zodResolver(customerSchema),
  });

  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [formMode, setFormMode] = useState<FormMode | undefined>(undefined);

  const contextValue = useMemo(
    () => ({
      onSubmit,
      onTrack,
      onClose,
      showUnsavedDialog,
      setShowUnsavedDialog,
      formMode,
      setFormMode: (mode: FormMode) => setFormMode(mode),
    }),
    [onSubmit, onTrack, onClose, showUnsavedDialog, formMode],
  );

  return (
    <CustomerSaveContext.Provider value={contextValue}>
      <FormProvider {...form}>{children}</FormProvider>
    </CustomerSaveContext.Provider>
  );
};

export default CustomerFormProvider;
